import React, { forwardRef, ComponentType, ForwardedRef, useCallback, ChangeEvent, FocusEvent, ReactElement } from 'react';
import { ElementWithValue } from 'ia-react-core/~/interfaces/ElementWithValue';
import { FixedKeyboardEvent } from 'ia-react-core/~/interfaces/FixedKeyboardEvent';

import excludePropsByComponent from '../../../utils/components/#excludePropsByComponent';
import Control, { ControlProps, ControlRenderProps } from '../Control';
import TextInput, { TextInputProps } from '../TextInput';

export type TextControlValidationProps = {
  /** Adds the `required` validator to the control. The control will be invalid if the value is empty. */
  required?: boolean;
  /** Adds the `recommended` validator to the control. The control will display a warning if the value is empty. */
  recommended?: boolean;
  /** Defines the "minLength" validator. */
  minLength?: number;
};

export interface TextControlProps<
  Value = string,
  StateValue = string
> extends
    Omit<TextInputProps, 'defaultValue' | 'name' | 'value' | 'id' | 'isValid' | 'isTouched' | 'ref' | 'inputRef'>,
    Omit<ControlProps<Value, StateValue, HTMLInputElement, TextControlValidationProps>, 'children' | 'id'>,
    TextControlValidationProps {

  /** Trims spaces from both end of the input. */
  trim?: boolean;
  /** Element type to use as the input. By default [TextInput](/Core/Components/Form/TextInput) */
  Input?: ComponentType<any>;
  /** @ignore */
  ref?: ForwardedRef<HTMLInputElement>;
}

interface TextControlInternalProps<
  Value
> extends Omit<ControlRenderProps<Value, HTMLInputElement>, 'onChange' | 'onBlur' | 'onKeyDown'> {
  onChangeCallback: ControlRenderProps<Value, HTMLInputElement>['onChange'];
  onBlurCallback: ControlRenderProps<Value, HTMLInputElement>['onBlur'];
  onKeyDownCallback: ControlRenderProps<Value, HTMLInputElement>['onKeyDown'];
}

export interface TextControlComposedProps<
  Value,
  StateValue
> extends TextControlProps<Value, StateValue>, TextControlInternalProps<Value> {

}

export const TextControlDefaultProps: Partial<TextControlProps<string, string>> = {
  trim: true,
  Input: TextInput,
};

const TextControl = Object.assign(
  forwardRef(<
    Value = string,
    StateValue = string,
  >({
      id,
      name,
      trim,
      required,
      readOnly,
      textOnly,
      minLength,
      defaultValidators,
      valueToStateFormatters,
      Input,
      isValid,
      isTouched,
      errorMessagesId,
      value,
      inputRef,
      isUnionControlGroup,
      onChange,
      onBlur,
      onKeyDown,
      registerControlGroup,
      unregisterControlGroup,
      onChangeCallback,
      onBlurCallback,
      onKeyDownCallback,
      ...props
    }: TextControlComposedProps<Value, StateValue>, ref: ForwardedRef<HTMLInputElement>) => {
    const handleChange = useCallback((event: ChangeEvent<ElementWithValue<Value>>) => {
      onChangeCallback(event);
      if (onChange) onChange(event as unknown as ChangeEvent<HTMLInputElement>);
    }, [onChange, onChangeCallback]);

    const handleBlur = useCallback((event: FocusEvent<ElementWithValue<Value>>) => {
      onBlurCallback(event);
      if (onBlur) onBlur(event as unknown as FocusEvent<HTMLInputElement>);
    }, [onBlur, onBlurCallback]);

    const handleKeydown = useCallback((event: FixedKeyboardEvent) => {
      onKeyDownCallback(event);
      if (onKeyDown) onKeyDown(event);
    }, [onKeyDownCallback, onKeyDown]);

    return (
      <Input
        id={id}
        data-testid="TextControl"
        aria-invalid={Boolean(!isValid && isTouched)}
        aria-errormessage={errorMessagesId}
        data-control-nesting="1"
        readOnly={readOnly}
        textOnly={textOnly}
        {...excludePropsByComponent(props, Control)}
        name={name}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeydown}
        isValid={isValid}
        isTouched={isTouched}
        inputRef={inputRef}
        isUnionControlGroup={isUnionControlGroup}
        ref={ref}
      />
    );
  }) as (<Value = string, StateValue = string>(props: TextControlComposedProps<Value, StateValue>) => ReactElement<TextControlComposedProps<Value, StateValue>>),
  {
    labeledControl: true,
    defaultProps: TextControlDefaultProps,
  },
);

export default TextControl;
